/** Label names the weakest evidence in the total. */
import type { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import type { useTranslation } from "src/i18n";
import type { WeightTotal } from "./shelfWeight";
import { shelfLimit, shelfLoad, shelfOverloadRatio } from "./shelfWeight";

type CommonT = ReturnType<typeof useTranslation<"common">>["t"];

export const formatWeightTotal = (total: WeightTotal, t: CommonT): string => {
  const pounds = total.pounds.toFixed(1);
  const notes: string[] = [];

  if (total.derivedSkus > 0) {
    notes.push(
      total.measuredSkus === 0
        ? t("shelfWeight.allDerived")
        : t("shelfWeight.someDerived", { count: total.derivedSkus }),
    );
  }

  if (total.unknownSkus > 0) {
    notes.push(t("shelfWeight.unweighed", { count: total.unknownSkus }));
  }

  return notes.length === 0
    ? t("shelfWeight.load", { pounds })
    : t("shelfWeight.loadWithNotes", { pounds, notes: notes.join(", ") });
};

/**
 * The shelf's weight verdict, or null when there is nothing to report.
 *
 * Three outcomes, not two. "No cap supplied" must not render as silence: an
 * omitted row is byte-identical to a shelf sitting comfortably under a real
 * limit, which is the one reading this feature exists to prevent.
 */
export const shelfWeightVerdict = (
  shelf: PlanogramShelf,
  t: CommonT,
): { label: string; value: string } | null => {
  const limit = shelfLimit(shelf);

  if (!limit) {
    return { label: t("shelfWeight.limitLabel"), value: t("shelfWeight.noLimit") };
  }

  const ratio = shelfOverloadRatio(shelf);
  if (ratio === null || ratio <= 1) {
    return null;
  }

  return {
    label: t("shelfWeight.overloadedLabel"),
    value: t(
      limit.source === "shelf"
        ? "shelfWeight.overloadedFixture"
        : "shelfWeight.overloadedRequestDefault",
      { pounds: limit.pounds },
    ),
  };
};

/**
 * The full set of weight rows for a shelf, in display order — the load row
 * plus the verdict row when there is one. Both the hover panel and the
 * shelves panel render this same array so they cannot disagree about what a
 * shelf weighs.
 */
export const shelfWeightRows = (
  shelf: PlanogramShelf,
  t: CommonT,
): { label: string; value: string }[] => {
  const loadLabel = formatWeightTotal(shelfLoad(shelf), t);
  const verdict = shelfWeightVerdict(shelf, t);

  return [{ label: t("shelfWeight.label"), value: loadLabel }, ...(verdict ? [verdict] : [])];
};

/** Null when unknown so callers hide the row instead of printing a guess. */
export const formatUnitWeight = (
  pounds: number | null,
  provenance: "measured" | "derived" | null,
  isBulk: boolean,
  t: CommonT,
): string | null => {
  if (pounds === null || provenance === null) {
    return null;
  }

  const source =
    provenance === "measured" ? t("shelfWeight.measured") : t("shelfWeight.allDerived");

  return `${pounds.toFixed(2)} lb · ${source}${isBulk ? ` · ${t("shelfWeight.bulk")}` : ""}`;
};
